import { DeviceType, useDevice } from "@/common/hooks/use-device";
import { ReactNode } from "react";

interface DeviceAwareProps {
  /** Content to render on mobile devices only */
  mobile?: ReactNode;
  /** Content to render on tablet devices only */
  tablet?: ReactNode;
  /** Content to render on desktop devices only */
  desktop?: ReactNode;
  /** Content to render on widescreen devices only */
  widescreen?: ReactNode;
  /** Content to render on both mobile and tablet devices */
  mobileTablet?: ReactNode;
  /** Content to render on both desktop and widescreen devices */
  desktopWidescreen?: ReactNode;
  /** Fallback content if no device-specific content matches */
  fallback?: ReactNode;
}

/**
 * A component that conditionally renders content based on the current device type.
 * Supports specific device types or device groups for responsive layouts.
 *
 * @example
 * <DeviceAware
 *   mobile={<MobileHeader />}
 *   desktopWidescreen={<DesktopHeader />}
 * />
 *
 * @example
 * <DeviceAware
 *   mobileTablet={<CompactView />}
 *   desktopWidescreen={<DetailedView />}
 * />
 */
export function DeviceAware({
  mobile,
  tablet,
  desktop,
  widescreen,
  mobileTablet,
  desktopWidescreen,
  fallback = null,
}: DeviceAwareProps) {
  const { deviceType } = useDevice();

  if (deviceType === DeviceType.Mobile) {
    return <>{mobile ?? mobileTablet ?? fallback}</>;
  }

  if (deviceType === DeviceType.Tablet) {
    return <>{tablet ?? mobileTablet ?? fallback}</>;
  }

  if (deviceType === DeviceType.Desktop) {
    return <>{desktop ?? desktopWidescreen ?? fallback}</>;
  }

  if (deviceType === DeviceType.Widescreen) {
    return <>{widescreen ?? desktopWidescreen ?? fallback}</>;
  }

  return <>{fallback}</>;
}
